import { useState, useCallback, useEffect } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { UserApiTokenRow } from '../UserApiTokenRow'
import { useUserApiTokenRepository } from '../../../../services/adaptors/userApiTokenRepositoryAdaptor'
import { CreateUserApiTokenInput } from '../../../../domain/entity/UserApiTokenEntity'
import { ApiTokenScope } from '../../../../domain/entity/ApiTokenScope'
import store from '../../../../store'
import { addScreenMessage, MessageLevel } from '../../../../store/messages'
import { intl } from '../../../../i18n'

type CreateTokenInput = {
  tokenName: string
  expiredAt: number
  scopes: string[]
}

type UseUserApiTokenDataReturn = {
  rowData: UserApiTokenRow[]
  availableScopes: ApiTokenScope[]
  loading: boolean
  onReload: () => Promise<void>
  onCreateToken: (input: CreateTokenInput) => Promise<string | null>
  onRevokeToken: (uuid: string) => Promise<boolean>
}

export const useUserApiTokenData = (
  functionUuid: string
): UseUserApiTokenDataReturn => {
  const [rowData, setRowData] = useState<UserApiTokenRow[]>([])
  const [availableScopes, setAvailableScopes] = useState<ApiTokenScope[]>([])
  const [loading, setLoading] = useState(false)
  const repository = useUserApiTokenRepository()

  const onReload = useCallback(async () => {
    setLoading(true)
    try {
      const [tokens, scopes] = await Promise.all([
        repository.getOwn(),
        repository.getScopes(),
      ])
      const rows: UserApiTokenRow[] = tokens.map(
        ({
          uuid,
          tokenName,
          scopes,
          expiredAt,
          createdAt,
          lastUsedAt,
          revoked,
          expired,
        }) => ({
          uuid,
          tokenName,
          scopes,
          expiredAt,
          createdAt,
          lastUsedAt,
          revoked,
          expired,
        })
      )
      setRowData(rows)
      setAvailableScopes(scopes.sort((a, b) => a.sortOrder - b.sortOrder))
    } catch (error) {
      store.dispatch(
        addScreenMessage(functionUuid, {
          type: MessageLevel.ERROR,
          title: intl.formatMessage({
            id: 'userApiToken.error.load',
          }),
        })
      )
    } finally {
      setLoading(false)
    }
  }, [repository, functionUuid])

  const onCreateToken = useCallback(
    async ({
      tokenName,
      expiredAt,
      scopes,
    }: CreateTokenInput): Promise<string | null> => {
      setLoading(true)
      try {
        const createInput: CreateUserApiTokenInput = {
          uuid: uuidv4(),
          tokenName,
          expiredAt,
          scopes,
        }
        const { rawToken } = await repository.create(createInput)
        await onReload()
        store.dispatch(
          addScreenMessage(functionUuid, {
            type: MessageLevel.SUCCESS,
            title: intl.formatMessage({
              id: 'userApiToken.success.create',
            }),
          })
        )
        return rawToken
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Unknown error'
        const isDuplicate = errorMessage.includes('duplicated')
        if (isDuplicate) {
          store.dispatch(
            addScreenMessage(functionUuid, {
              type: MessageLevel.WARN,
              title: intl.formatMessage({
                id: 'userApiToken.error.duplicateTokenName',
              }),
            })
          )
        } else {
          store.dispatch(
            addScreenMessage(functionUuid, {
              type: MessageLevel.ERROR,
              title: intl.formatMessage({
                id: 'userApiToken.error.create',
              }),
            })
          )
        }
        return null
      } finally {
        setLoading(false)
      }
    },
    [repository, functionUuid, onReload]
  )

  const onRevokeToken = useCallback(
    async (uuid: string): Promise<boolean> => {
      setLoading(true)
      try {
        await repository.revoke(uuid)
        await onReload()
        store.dispatch(
          addScreenMessage(functionUuid, {
            type: MessageLevel.SUCCESS,
            title: intl.formatMessage({
              id: 'userApiToken.success.revoke',
            }),
          })
        )
        return true
      } catch (error) {
        store.dispatch(
          addScreenMessage(functionUuid, {
            type: MessageLevel.ERROR,
            title: intl.formatMessage({
              id: 'userApiToken.error.revoke',
            }),
          })
        )
        return false
      } finally {
        setLoading(false)
      }
    },
    [repository, functionUuid, onReload]
  )

  useEffect(() => {
    onReload()
  }, [onReload])

  return {
    rowData,
    availableScopes,
    loading,
    onReload,
    onCreateToken,
    onRevokeToken,
  }
}
